import type maplibregl from 'maplibre-gl';

/**
 * Render groups ordered bottom-to-top.
 * When adding a new Orbit layer, pick the group that best describes what the layer
 * renders — `addOrbitLayer` will insert it immediately before the first layer in any
 * group above, keeping all groups in the correct visual stacking order.
 *
 * Groups:
 *   areaOverlays    — filled polygons (grid tiles, DMAs, surveyed areas, network boundary)
 *   lineOverlays    — lines on the network (pipelines)
 *   selectionRadius — the selected-POI search circle (fill, rim decorations, dashed
 *                     boundary) and the clipped in-radius pipe sections. Sits directly
 *                     above the base pipelines it emphasises but below every point-like
 *                     surface, so fittings, clusters and markers inside the circle stay
 *                     fully visible on top of the highlight.
 *   assetOverlays   — point-like assets (fittings, technician vans)
 *   poiClusters     — clustered POI and air-valve circles and counts
 *   pois            — individual POI icons
 *   businessLabels  — text labels for network features and POIs
 *   selectionSpotlight — the translucent mask that dims everything rendered below it
 *                     (basemap included) outside the selected POI's search circle. Sits
 *                     above the markers so features outside the circle are washed out,
 *                     and below `interaction` so the selected marker, its halo and the
 *                     tool overlays stay full-strength above the mask.
 *   interaction     — invisible hover/selection highlight layers (always on top)
 */
export const MAP_LAYER_GROUPS = [
    'areaOverlays',
    'lineOverlays',
    'selectionRadius',
    'assetOverlays',
    'poiClusters',
    'pois',
    'businessLabels',
    'selectionSpotlight',
    'interaction',
] as const;

export type MapLayerGroup = (typeof MAP_LAYER_GROUPS)[number];

/** Stable MapLibre layer ID constants for every planned Orbit layer. */
export const ORBIT_LAYER_IDS = {
    // areaOverlays
    gridTiles: 'orbit-grid-tiles',
    gridTilesFill: 'orbit-grid-tiles-fill',
    gridTilesOutline: 'orbit-grid-tiles-outline',
    dmaFill: 'orbit-dma-fill',
    dmaOutline: 'orbit-dma-outline',
    dmaSelectedFill: 'orbit-dma-selected-fill',
    dmaSelectedOutline: 'orbit-dma-selected-outline',
    surveyedAreas: 'orbit-surveyed-areas',
    surveyedAreasFill: 'orbit-surveyed-areas-fill',
    surveyedAreasOutline: 'orbit-surveyed-areas-outline',
    surveyedAreasSelectedFill: 'orbit-surveyed-areas-selected-fill',
    surveyedAreasSelectedOutline: 'orbit-surveyed-areas-selected-outline',
    networkBoundary: 'orbit-network-boundary',
    // lineOverlays
    pipelinesCase: 'orbit-pipelines-case',
    pipelines: 'orbit-pipelines',
    // assetOverlays
    fittingsShadow: 'orbit-fittings-shadow',
    fittings: 'orbit-fittings',
    technicianVanHaloOuter: 'orbit-technician-van-halo-outer',
    technicianVanHaloInner: 'orbit-technician-van-halo-inner',
    technicianVans: 'orbit-technician-vans',
    // poiClusters
    poiClusters: 'orbit-poi-clusters',
    poiClusterCount: 'orbit-poi-cluster-count',
    // network-level POI clusters (all-network lightweight source, visible below the DMA band);
    // unclustered isolated POIs render as "1" bubbles in the same style — never as pins.
    poiNetworkClusters: 'orbit-poi-network-clusters',
    poiNetworkClusterCount: 'orbit-poi-network-cluster-count',
    poiNetworkUnclustered: 'orbit-poi-network-unclustered',
    // Network Explorer: the estate's pre-aggregated POI/leak cells, clustered client-side.
    // Every feature is an aggregate (a lone cell renders like a cluster), so there is no
    // unclustered variant. Same elevation as the single-client bubbles — never shown together.
    explorerClusters: 'orbit-explorer-clusters',
    explorerClusterCount: 'orbit-explorer-cluster-count',
    // air-valve clusters (savi) — rendered at the same elevation as POI clusters (the
    // two products are never shown together: POIs on Find & Fix, air valves on SAVI).
    airValvesClusters: 'orbit-air-valves-clusters',
    airValvesClusterCount: 'orbit-air-valves-cluster-count',
    airValvesHighRiskClusters: 'orbit-air-valves-high-risk-clusters',
    airValvesHighRiskClusterCount: 'orbit-air-valves-high-risk-cluster-count',
    // pois
    // The shared-origin fan base (dot + hairline under exactly-coincident stacks) renders
    // beneath the pins; leak entities render above POI entities so the cross-family
    // hit-test winner is deterministic by layer order. Within-group order is registration
    // order: fan base first, then pois, then leaks.
    entityFanBase: 'orbit-entity-fan-base',
    pois: 'orbit-pois',
    leaks: 'orbit-leaks',
    // air valves (savi) — rendered at the same elevation as POI icons (the two
    // products are never shown together: POIs on Find & Fix, air valves on SAVI).
    airValves: 'orbit-air-valves',
    airValvesHighRisk: 'orbit-air-valves-high-risk',
    // businessLabels
    gridTilesLabels: 'orbit-grid-tiles-labels',
    pipelineLabels: 'orbit-pipeline-labels',
    poiLabels: 'orbit-poi-labels',
    areaLabels: 'orbit-area-labels',
    dmaLabels: 'orbit-dma-labels',
    // selectionRadius + selectionSpotlight (one shared source, features discriminated
    // by `part` — the mask alone renders in the spotlight group above the markers)
    poiRadiusMask: 'orbit-poi-radius-mask',
    poiRadiusFill: 'orbit-poi-radius-fill',
    poiRadiusInnerShadow: 'orbit-poi-radius-inner-shadow',
    poiRadiusInnerRing: 'orbit-poi-radius-inner-ring',
    poiRadiusOutline: 'orbit-poi-radius-outline',
    poiRadiusPipelines: 'orbit-poi-radius-pipelines',
    // interaction
    hover: 'orbit-hover',
    selected: 'orbit-selected',
    highlight: 'orbit-highlight',
    poiSelectedHaloOuter: 'orbit-poi-selected-halo-outer',
    poiSelectedHaloInner: 'orbit-poi-selected-halo-inner',
    poiSelected: 'orbit-poi-selected',
    // selected-trip route display (one shared source, features discriminated by `part`)
    vehicleTripRoute: 'orbit-vehicle-trip-route',
    vehicleTripRoutePoints: 'orbit-vehicle-trip-route-points',
    // radial measuring tool (one shared source, features discriminated by `part`)
    radialFill: 'orbit-radial-fill',
    radialOutline: 'orbit-radial-outline',
    radialLine: 'orbit-radial-line',
    radialCentre: 'orbit-radial-centre',
    radialHandleHit: 'orbit-radial-handle-hit',
    radialHandle: 'orbit-radial-handle',
} as const;

/**
 * Layers that suppress AOI/DMA polygon selection when they render a feature above the
 * click point. The fill layers' click handlers fire even when a marker sits on top, so
 * polygon selection must explicitly stand down when the user's visible target was the
 * marker, cluster bubble, count label, line or fitting above the fill.
 *
 * This is deliberately NOT a list of "all interactive layers" — it answers exactly one
 * question: does a rendered surface capture the click away from the polygon underneath?
 * Every Orbit layer must appear either here or in the exemption map below (with a
 * reason); the completeness test enforces that every new layer makes this decision
 * consciously.
 */
export const FEATURE_SELECTION_OCCLUDING_LAYER_IDS: readonly string[] = [
    ORBIT_LAYER_IDS.pois,
    ORBIT_LAYER_IDS.leaks,
    ORBIT_LAYER_IDS.poiClusters,
    ORBIT_LAYER_IDS.poiClusterCount,
    ORBIT_LAYER_IDS.poiNetworkClusters,
    ORBIT_LAYER_IDS.poiNetworkClusterCount,
    ORBIT_LAYER_IDS.poiNetworkUnclustered,
    ORBIT_LAYER_IDS.explorerClusters,
    ORBIT_LAYER_IDS.explorerClusterCount,
    ORBIT_LAYER_IDS.airValves,
    ORBIT_LAYER_IDS.airValvesHighRisk,
    ORBIT_LAYER_IDS.airValvesClusters,
    ORBIT_LAYER_IDS.airValvesClusterCount,
    ORBIT_LAYER_IDS.airValvesHighRiskClusters,
    ORBIT_LAYER_IDS.airValvesHighRiskClusterCount,
    ORBIT_LAYER_IDS.pipelines,
    ORBIT_LAYER_IDS.fittings,
    ORBIT_LAYER_IDS.technicianVans,
];

/**
 * Layers explicitly exempt from polygon-selection occlusion, each with the reason the
 * decision was made. Keyed by layer id; the completeness test requires every Orbit layer
 * to appear in exactly one of the occluding list or this map.
 */
export const FEATURE_SELECTION_OCCLUSION_EXEMPT_LAYER_IDS: Readonly<Record<string, string>> = {
    [ORBIT_LAYER_IDS.dmaFill]: 'the DMA selection surface itself',
    [ORBIT_LAYER_IDS.surveyedAreasFill]: 'the AOI selection surface itself',
    [ORBIT_LAYER_IDS.pipelinesCase]: 'visual casing beneath pipelines — the pipeline layer itself occludes',
    [ORBIT_LAYER_IDS.fittingsShadow]: 'decorative shadow beneath fittings — the fitting layer itself occludes',
    [ORBIT_LAYER_IDS.gridTiles]: 'overview polygon context below the selectable fills',
    [ORBIT_LAYER_IDS.gridTilesFill]: 'overview polygon context below the selectable fills',
    [ORBIT_LAYER_IDS.gridTilesOutline]: 'overview polygon context below the selectable fills',
    [ORBIT_LAYER_IDS.networkBoundary]: 'overview polygon context below the selectable fills',
    [ORBIT_LAYER_IDS.dmaOutline]: 'outline of the selection surface, not a capture surface',
    [ORBIT_LAYER_IDS.surveyedAreasOutline]: 'outline of the selection surface, not a capture surface',
    [ORBIT_LAYER_IDS.surveyedAreas]: 'legacy container id for the surveyed-areas overlay, not a capture surface',
    [ORBIT_LAYER_IDS.dmaSelectedFill]: 'selection feedback mirroring an already-selected polygon',
    [ORBIT_LAYER_IDS.dmaSelectedOutline]: 'selection feedback mirroring an already-selected polygon',
    [ORBIT_LAYER_IDS.surveyedAreasSelectedFill]: 'selection feedback mirroring an already-selected polygon',
    [ORBIT_LAYER_IDS.surveyedAreasSelectedOutline]: 'selection feedback mirroring an already-selected polygon',
    [ORBIT_LAYER_IDS.gridTilesLabels]: 'non-interactive text label',
    [ORBIT_LAYER_IDS.pipelineLabels]: 'non-interactive text label',
    [ORBIT_LAYER_IDS.poiLabels]: 'non-interactive text label',
    [ORBIT_LAYER_IDS.areaLabels]: 'non-interactive text label',
    [ORBIT_LAYER_IDS.dmaLabels]: 'non-interactive text label',
    [ORBIT_LAYER_IDS.hover]: 'hover feedback mirroring a feature another layer already owns',
    [ORBIT_LAYER_IDS.selected]: 'selection feedback mirroring a feature another layer already owns',
    [ORBIT_LAYER_IDS.highlight]: 'highlight feedback mirroring a feature another layer already owns',
    [ORBIT_LAYER_IDS.poiSelectedHaloOuter]: 'selection feedback mirroring the selected POI',
    [ORBIT_LAYER_IDS.poiSelectedHaloInner]: 'selection feedback mirroring the selected POI',
    [ORBIT_LAYER_IDS.poiSelected]: 'selection feedback mirroring the selected POI',
    [ORBIT_LAYER_IDS.technicianVanHaloOuter]: 'selection feedback mirroring the selected van',
    [ORBIT_LAYER_IDS.technicianVanHaloInner]: 'selection feedback mirroring the selected van',
    [ORBIT_LAYER_IDS.poiRadiusMask]: 'display-only dimming wash for the selected POI radius',
    [ORBIT_LAYER_IDS.poiRadiusFill]: 'display-only search-circle fill for the selected POI',
    [ORBIT_LAYER_IDS.poiRadiusInnerShadow]: 'display-only search-circle decoration for the selected POI',
    [ORBIT_LAYER_IDS.poiRadiusInnerRing]: 'display-only search-circle decoration for the selected POI',
    [ORBIT_LAYER_IDS.poiRadiusOutline]: 'display-only search-circle decoration for the selected POI',
    [ORBIT_LAYER_IDS.poiRadiusPipelines]:
        'display-only clipped pipe sections for the selected POI — the base pipeline layer owns occlusion',
    [ORBIT_LAYER_IDS.entityFanBase]:
        'decorative shared-origin indicator beneath coincident-stack markers — the markers themselves occlude',
    [ORBIT_LAYER_IDS.vehicleTripRoute]: 'display-only route overlay for an already-selected trip',
    [ORBIT_LAYER_IDS.vehicleTripRoutePoints]: 'display-only route markers for an already-selected trip',
    [ORBIT_LAYER_IDS.radialFill]: 'radial-tool geometry — polygon selection is suppressed while the tool is active',
    [ORBIT_LAYER_IDS.radialOutline]: 'radial-tool geometry — polygon selection is suppressed while the tool is active',
    [ORBIT_LAYER_IDS.radialLine]: 'radial-tool geometry — polygon selection is suppressed while the tool is active',
    [ORBIT_LAYER_IDS.radialCentre]: 'radial-tool geometry — polygon selection is suppressed while the tool is active',
    [ORBIT_LAYER_IDS.radialHandleHit]:
        'radial-tool drag target — polygon selection is suppressed while the tool is active',
    [ORBIT_LAYER_IDS.radialHandle]: 'radial-tool geometry — polygon selection is suppressed while the tool is active',
};

/**
 * The layers whose rendered feature under a click means the click is NOT empty, grouped
 * by the kind of feature interaction that click begins. One global listener classifies
 * every click against these categories (see useMapClickDismissal) and owns all
 * cross-feature clearing; the feature layers' own delegated handlers keep selection,
 * pinning and cluster-zoom ownership.
 *
 * Deliberately a different question from occlusion above, so the two lists must never
 * merge: pipelines and fittings occlude polygon selection but have no click behaviour of
 * their own, so a click on one over open ground stays an empty click — exactly today's
 * behaviour, preserved on purpose.
 */
export const GLOBAL_CLICK_LAYER_CATEGORIES = {
    // Leak entities share the POI category: both open a details panel through the same
    // single-winner hit rule, so a click on either is equally non-empty.
    poi: [ORBIT_LAYER_IDS.pois, ORBIT_LAYER_IDS.leaks],
    valve: [ORBIT_LAYER_IDS.airValves, ORBIT_LAYER_IDS.airValvesHighRisk],
    // The explorer's aggregate bubbles share the category: a click on one opens its
    // breakdown card through its own delegated handler and is equally non-empty.
    poiCluster: [
        ORBIT_LAYER_IDS.poiClusters,
        ORBIT_LAYER_IDS.poiClusterCount,
        ORBIT_LAYER_IDS.poiNetworkClusters,
        ORBIT_LAYER_IDS.poiNetworkClusterCount,
        ORBIT_LAYER_IDS.poiNetworkUnclustered,
        ORBIT_LAYER_IDS.explorerClusters,
        ORBIT_LAYER_IDS.explorerClusterCount,
    ],
    valveCluster: [
        ORBIT_LAYER_IDS.airValvesClusters,
        ORBIT_LAYER_IDS.airValvesClusterCount,
        ORBIT_LAYER_IDS.airValvesHighRiskClusters,
        ORBIT_LAYER_IDS.airValvesHighRiskClusterCount,
    ],
    van: [ORBIT_LAYER_IDS.technicianVans],
    polygon: [ORBIT_LAYER_IDS.dmaFill, ORBIT_LAYER_IDS.surveyedAreasFill],
} as const;

export type GlobalClickCategory = keyof typeof GLOBAL_CLICK_LAYER_CATEGORIES;

/** Flat view of every layer whose rendered feature makes a click non-empty. */
export const GLOBAL_CLICK_HANDLED_LAYER_IDS: readonly string[] = Object.values(GLOBAL_CLICK_LAYER_CATEGORIES).flat();

/**
 * Layers explicitly excluded from the non-empty decision, each with the reason. A click
 * whose only rendered features come from these layers is a truly empty click. Keyed by
 * layer id; the completeness test requires every Orbit layer to appear in exactly one of
 * the category map or this map.
 */
export const GLOBAL_CLICK_EXEMPT_LAYER_IDS: Readonly<Record<string, string>> = {
    [ORBIT_LAYER_IDS.pipelines]: 'pipelines have hover only — a pipeline click over open ground stays empty',
    [ORBIT_LAYER_IDS.pipelinesCase]: 'visual casing beneath pipelines',
    [ORBIT_LAYER_IDS.fittings]: 'fittings have hover only — a fitting click over open ground stays empty',
    [ORBIT_LAYER_IDS.fittingsShadow]: 'decorative shadow beneath fittings',
    [ORBIT_LAYER_IDS.gridTiles]: 'overview polygon context with no click behaviour',
    [ORBIT_LAYER_IDS.gridTilesFill]: 'overview polygon context with no click behaviour',
    [ORBIT_LAYER_IDS.gridTilesOutline]: 'overview polygon context with no click behaviour',
    [ORBIT_LAYER_IDS.networkBoundary]: 'overview polygon context with no click behaviour',
    [ORBIT_LAYER_IDS.dmaOutline]: 'outline decoration for the DMA fill, which is the handled surface',
    [ORBIT_LAYER_IDS.surveyedAreasOutline]: 'outline decoration for the AOI fill, which is the handled surface',
    [ORBIT_LAYER_IDS.surveyedAreas]: 'legacy container id for the surveyed-areas overlay',
    [ORBIT_LAYER_IDS.dmaSelectedFill]:
        'selection feedback — clicking a selected DMA where its base fill is hidden clears, matching today',
    [ORBIT_LAYER_IDS.dmaSelectedOutline]: 'selection feedback for an already-selected DMA',
    [ORBIT_LAYER_IDS.surveyedAreasSelectedFill]:
        'selection feedback — clicking a selected AOI where its base fill is hidden clears, matching today',
    [ORBIT_LAYER_IDS.surveyedAreasSelectedOutline]: 'selection feedback for an already-selected AOI',
    [ORBIT_LAYER_IDS.gridTilesLabels]: 'non-interactive text label',
    [ORBIT_LAYER_IDS.pipelineLabels]: 'non-interactive text label',
    [ORBIT_LAYER_IDS.poiLabels]:
        'non-interactive text label — clicking a POI label outside the icon hit area is empty today',
    [ORBIT_LAYER_IDS.areaLabels]: 'non-interactive text label',
    [ORBIT_LAYER_IDS.dmaLabels]: 'non-interactive text label',
    [ORBIT_LAYER_IDS.hover]: 'hover feedback mirroring a feature another layer already owns',
    [ORBIT_LAYER_IDS.selected]: 'selection feedback mirroring a feature another layer already owns',
    [ORBIT_LAYER_IDS.highlight]: 'highlight feedback mirroring a feature another layer already owns',
    [ORBIT_LAYER_IDS.poiSelectedHaloOuter]: 'selection feedback mirroring the selected POI',
    [ORBIT_LAYER_IDS.poiSelectedHaloInner]: 'selection feedback mirroring the selected POI',
    [ORBIT_LAYER_IDS.poiSelected]: 'selection feedback mirroring the selected POI',
    [ORBIT_LAYER_IDS.technicianVanHaloOuter]: 'selection feedback mirroring the selected van',
    [ORBIT_LAYER_IDS.technicianVanHaloInner]: 'selection feedback mirroring the selected van',
    [ORBIT_LAYER_IDS.poiRadiusMask]:
        'display-only dimming wash — a click through it lands on whatever it covers, or stays empty',
    [ORBIT_LAYER_IDS.poiRadiusFill]: 'display-only search-circle fill — clicking inside the circle behaves as normal',
    [ORBIT_LAYER_IDS.poiRadiusInnerShadow]: 'display-only search-circle decoration',
    [ORBIT_LAYER_IDS.poiRadiusInnerRing]: 'display-only search-circle decoration',
    [ORBIT_LAYER_IDS.poiRadiusOutline]: 'display-only search-circle decoration',
    [ORBIT_LAYER_IDS.poiRadiusPipelines]:
        'display-only clipped pipe sections — pipelines have no click behaviour of their own',
    [ORBIT_LAYER_IDS.entityFanBase]:
        'decorative shared-origin indicator — its markers are the click targets, the hairline over open ground stays empty',
    [ORBIT_LAYER_IDS.vehicleTripRoute]:
        'display-only route overlay — clicking the drawn route over open ground stays empty',
    [ORBIT_LAYER_IDS.vehicleTripRoutePoints]: 'display-only route markers — clicking one over open ground stays empty',
    [ORBIT_LAYER_IDS.radialFill]:
        'radial-tool geometry — the global click listener stands down while the tool owns the map',
    [ORBIT_LAYER_IDS.radialOutline]:
        'radial-tool geometry — the global click listener stands down while the tool owns the map',
    [ORBIT_LAYER_IDS.radialLine]:
        'radial-tool geometry — the global click listener stands down while the tool owns the map',
    [ORBIT_LAYER_IDS.radialCentre]:
        'radial-tool geometry — the global click listener stands down while the tool owns the map',
    [ORBIT_LAYER_IDS.radialHandleHit]:
        'radial-tool drag target — the global click listener stands down while the tool owns the map',
    [ORBIT_LAYER_IDS.radialHandle]:
        'radial-tool geometry — the global click listener stands down while the tool owns the map',
};

const LAYER_GROUP_REGISTRY: Record<MapLayerGroup, string[]> = {
    areaOverlays: [
        ORBIT_LAYER_IDS.gridTilesFill,
        ORBIT_LAYER_IDS.gridTilesOutline,
        ORBIT_LAYER_IDS.gridTiles,
        ORBIT_LAYER_IDS.dmaFill,
        ORBIT_LAYER_IDS.dmaOutline,
        ORBIT_LAYER_IDS.dmaSelectedFill,
        ORBIT_LAYER_IDS.dmaSelectedOutline,
        ORBIT_LAYER_IDS.surveyedAreas,
        ORBIT_LAYER_IDS.surveyedAreasFill,
        ORBIT_LAYER_IDS.surveyedAreasOutline,
        ORBIT_LAYER_IDS.surveyedAreasSelectedFill,
        ORBIT_LAYER_IDS.surveyedAreasSelectedOutline,
        ORBIT_LAYER_IDS.networkBoundary,
    ],
    lineOverlays: [ORBIT_LAYER_IDS.pipelinesCase, ORBIT_LAYER_IDS.pipelines],
    selectionRadius: [
        ORBIT_LAYER_IDS.poiRadiusFill,
        ORBIT_LAYER_IDS.poiRadiusInnerShadow,
        ORBIT_LAYER_IDS.poiRadiusInnerRing,
        ORBIT_LAYER_IDS.poiRadiusOutline,
        ORBIT_LAYER_IDS.poiRadiusPipelines,
    ],
    assetOverlays: [
        ORBIT_LAYER_IDS.fittingsShadow,
        ORBIT_LAYER_IDS.fittings,
        // Halos register before the van symbol layer so the marker renders on top.
        ORBIT_LAYER_IDS.technicianVanHaloOuter,
        ORBIT_LAYER_IDS.technicianVanHaloInner,
        ORBIT_LAYER_IDS.technicianVans,
    ],
    poiClusters: [
        ORBIT_LAYER_IDS.poiClusters,
        ORBIT_LAYER_IDS.poiClusterCount,
        ORBIT_LAYER_IDS.poiNetworkClusters,
        ORBIT_LAYER_IDS.poiNetworkClusterCount,
        ORBIT_LAYER_IDS.poiNetworkUnclustered,
        ORBIT_LAYER_IDS.explorerClusters,
        ORBIT_LAYER_IDS.explorerClusterCount,
        ORBIT_LAYER_IDS.airValvesClusters,
        ORBIT_LAYER_IDS.airValvesClusterCount,
        ORBIT_LAYER_IDS.airValvesHighRiskClusters,
        ORBIT_LAYER_IDS.airValvesHighRiskClusterCount,
    ],
    pois: [
        ORBIT_LAYER_IDS.entityFanBase,
        ORBIT_LAYER_IDS.pois,
        ORBIT_LAYER_IDS.leaks,
        ORBIT_LAYER_IDS.airValves,
        ORBIT_LAYER_IDS.airValvesHighRisk,
    ],
    businessLabels: [
        ORBIT_LAYER_IDS.gridTilesLabels,
        ORBIT_LAYER_IDS.pipelineLabels,
        ORBIT_LAYER_IDS.poiLabels,
        ORBIT_LAYER_IDS.areaLabels,
        ORBIT_LAYER_IDS.dmaLabels,
    ],
    selectionSpotlight: [ORBIT_LAYER_IDS.poiRadiusMask],
    interaction: [
        ORBIT_LAYER_IDS.hover,
        ORBIT_LAYER_IDS.selected,
        ORBIT_LAYER_IDS.highlight,
        ORBIT_LAYER_IDS.poiSelectedHaloOuter,
        ORBIT_LAYER_IDS.poiSelectedHaloInner,
        ORBIT_LAYER_IDS.poiSelected,
        ORBIT_LAYER_IDS.vehicleTripRoute,
        ORBIT_LAYER_IDS.vehicleTripRoutePoints,
        ORBIT_LAYER_IDS.radialFill,
        ORBIT_LAYER_IDS.radialOutline,
        ORBIT_LAYER_IDS.radialLine,
        ORBIT_LAYER_IDS.radialCentre,
        ORBIT_LAYER_IDS.radialHandleHit,
        ORBIT_LAYER_IDS.radialHandle,
    ],
};

/**
 * Returns the first layer ID from `layerIds` that currently exists in the map,
 * or `undefined` if none are present.
 */
export function findFirstExistingLayerId(map: maplibregl.Map, layerIds: string[]): string | undefined {
    return layerIds.find((id) => !!map.getLayer(id));
}

/**
 * Returns the `beforeId` to pass to `map.addLayer()` so the new layer renders
 * immediately below all groups above `group` in the stack.
 *
 * Returns `undefined` when no layers from higher groups exist yet (the new layer
 * will be added on top of everything currently in the map).
 */
export function getLayerInsertBeforeId(map: maplibregl.Map, group: MapLayerGroup): string | undefined {
    const groupIndex = MAP_LAYER_GROUPS.indexOf(group);
    const groupsAbove = MAP_LAYER_GROUPS.slice(groupIndex + 1);
    for (const g of groupsAbove) {
        const found = findFirstExistingLayerId(map, LAYER_GROUP_REGISTRY[g]);
        if (found !== undefined) return found;
    }
    return undefined;
}

/**
 * Adds a MapLibre layer to the map, inserting it at the correct position for
 * `group`. Safe to call when the map is loaded — does not guard against
 * duplicate IDs (MapLibre will throw as usual in that case).
 */
export function addOrbitLayer(map: maplibregl.Map, layer: maplibregl.LayerSpecification, group: MapLayerGroup): void {
    const beforeId = getLayerInsertBeforeId(map, group);
    map.addLayer(layer, beforeId);
}
